/**
 * What of a project is worth keeping, and what a reload has to forget.
 *
 * Pure, so the rule is tested without a browser: a saved project must never resurrect a
 * download link that has gone, an approval of results nobody has seen again, or a job phase
 * that nothing is polling.
 */

import {
  DEFAULT_PAGE_SETUP,
  EMPTY_AREA,
  INITIAL_STATE,
  type AreaDraft,
  type ConversionState,
  type PageSetup,
} from "./state.ts";

const IN_FLIGHT = new Set(["submitting", "queued", "running"]);

/**
 * The state as it goes into storage.
 *
 * Created packages are blob URLs that die with the page, and the review is of specific results
 * that are fetched again on the way back in, so both are reset. An area whose job is still in
 * flight keeps its job id and phase: recognition runs for minutes, and a reload in the middle
 * is the case the old single-job resume existed for.
 */
export function snapshotState(state: ConversionState): ConversionState {
  const areas: Record<string, AreaDraft> = {};
  for (const [key, draft] of Object.entries(state.areas)) {
    const inFlight = IN_FLIGHT.has(draft.jobPhase) && draft.jobId !== null;
    areas[key] = {
      ...draft,
      jobPhase: inFlight ? "running" : draft.run !== null ? "succeeded" : "none",
      jobId: inFlight ? draft.jobId : null,
    };
  }
  return { ...state, areas, packagePhase: "none", createdPackages: [] };
}

/**
 * A stored value, accepted only if it has the shape of a state.
 *
 * Missing pieces fall back to the initial state rather than being repaired: a half-understood
 * project would put a polygon somewhere the operator did not draw it.
 */
export function restoreState(stored: unknown): ConversionState | null {
  if (typeof stored !== "object" || stored === null) return null;
  const candidate = stored as Partial<ConversionState>;
  if (typeof candidate.pages !== "object" || candidate.pages === null) return null;
  if (typeof candidate.areas !== "object" || candidate.areas === null) return null;
  // Nested records are filled from the defaults too, so a field added to a page or an area
  // after a project was saved comes back as its default instead of undefined, and an area
  // with no `run` is not mistaken for one that finished.
  const pages: Record<number, PageSetup> = {};
  for (const [key, page] of Object.entries(candidate.pages)) {
    pages[Number(key)] = { ...DEFAULT_PAGE_SETUP, ...page };
  }
  const areas: Record<string, AreaDraft> = {};
  for (const [key, area] of Object.entries(candidate.areas)) {
    areas[key] = { ...EMPTY_AREA, ...area, run: area.run ?? null };
  }
  return snapshotState({
    ...INITIAL_STATE,
    ...candidate,
    pages,
    areas,
    details: { ...INITIAL_STATE.details, ...candidate.details },
  });
}

/** Stored at the top level of a record, beside the file, so a list does not have to parse state. */
export interface ProjectSummary {
  readonly id: string;
  readonly name: string;
  readonly fileName: string;
  readonly updatedAt: string;
}

export function projectName(state: ConversionState, fileName: string): string {
  const school = state.details.schoolName.trim();
  return school === "" ? fileName : school;
}

export function newProjectId(): string {
  return crypto.randomUUID();
}
